import { t } from '@/lib/i18n';

/** Launchpad wizard step keys — match the API step names */
export const LAUNCHPAD_STEPS = [
  'operating_mode',
  'active_domains',
  'ai_provider',
  'locale_tz',
  // AC-PW2-2: persona_setup step removed from Launchpad. Server-side seeding
  // of the Virtual CEO actor + persona row still happens in _seedTenant.
  'cadence',
  'summary',
] as const;

export type LaunchpadStepKey = (typeof LAUNCHPAD_STEPS)[number];

/** Step labels for the step indicator */
export const STEP_LABELS: Record<LaunchpadStepKey, string> = {
  operating_mode: 'Operating Mode',
  active_domains: 'Active Domains',
  ai_provider: 'AI Provider',
  locale_tz: 'Locale & Theme',
  cadence: 'Cadence Schedule',
  summary: 'Summary',
};

// ─── Active Domain Entry (F-Meeting-1 Wave 1 — AC-Meeting1-3.1) ──────────────

export interface ActiveDomainEntry {
  domainId: number;
  ledByAi: boolean;
}

/** Normalize backward-compat active_domains data */
export function normalizeActiveDomains(data: Record<string, unknown>): ActiveDomainEntry[] {
  if (Array.isArray((data as any)?.active_domains)) {
    return ((data as any).active_domains as ActiveDomainEntry[]).map((e) => ({
      domainId: typeof e?.domainId === 'number' ? e.domainId : Number(e?.domainId),
      ledByAi: Boolean(e?.ledByAi),
    }));
  }
  if (Array.isArray((data as any)?.domain_ids)) {
    return ((data as any).domain_ids as number[]).map((domainId) => ({ domainId, ledByAi: false }));
  }
  return [{ domainId: 1, ledByAi: false }];
}

// ─── Delegation Envelope ──────────────────────────────────────────────────────

export const DELEGATION_ENVELOPE_VALUES = ['draft', 'inbox', 'silent', 'blocked', 'n/a'] as const;
export type DelegationEnvelopeValue = (typeof DELEGATION_ENVELOPE_VALUES)[number];

export type DelegationEnvelopeMode = {
  pilot: DelegationEnvelopeValue;
  copilot: DelegationEnvelopeValue;
  autopilot: DelegationEnvelopeValue;
};

export type DelegationEnvelope = {
  run_full_meeting: DelegationEnvelopeMode;
  edit_purpose: DelegationEnvelopeMode;
  edit_vision_values_principles: DelegationEnvelopeMode;
  commit_annual_goal: DelegationEnvelopeMode;
  commit_outcomes: DelegationEnvelopeMode;
  decide_outcome_disposition: DelegationEnvelopeMode;
  approve_vp_cascade: DelegationEnvelopeMode;
};

/** D-1 default envelope — pre-fills the matrix UI */
export const D1_DELEGATION_ENVELOPE: DelegationEnvelope = {
  run_full_meeting:              { pilot: 'blocked', copilot: 'blocked', autopilot: 'inbox' },
  edit_purpose:                  { pilot: 'draft',   copilot: 'draft',   autopilot: 'draft' },
  edit_vision_values_principles: { pilot: 'draft',   copilot: 'draft',   autopilot: 'inbox' },
  commit_annual_goal:            { pilot: 'draft',   copilot: 'draft',   autopilot: 'inbox' },
  commit_outcomes:               { pilot: 'draft',   copilot: 'inbox',   autopilot: 'silent' },
  decide_outcome_disposition:    { pilot: 'draft',   copilot: 'inbox',   autopilot: 'silent' },
  approve_vp_cascade:            { pilot: 'blocked', copilot: 'draft',   autopilot: 'inbox' },
};

export const ENVELOPE_ACTION_LABELS: Record<keyof DelegationEnvelope, string> = {
  run_full_meeting:              'Run Full Meeting',
  edit_purpose:                  'Edit Purpose',
  edit_vision_values_principles: 'Edit Vision/Values/Principles',
  commit_annual_goal:            'Commit Annual Goal',
  commit_outcomes:               'Commit Outcomes',
  decide_outcome_disposition:    'Decide Outcome Disposition',
  approve_vp_cascade:            'Approve VP Cascade',
};

// ─── Persona Step State ───────────────────────────────────────────────────────

export interface PersonaSetupState {
  voice_description: string;
  style_description: string;
  values_priorities: string;
  decision_style: string;
  // AC-PW1-5: domain_stances removed from launchpad onboarding. Managed only via Settings → Virtual CEO.
  delegation_envelope: DelegationEnvelope;
  primary_language: string;
  language_overrides: { team?: string; external?: string; mixed?: string };
  language_rules: string;
}

export const PERSONA_SETUP_DEFAULTS: PersonaSetupState = {
  voice_description: '',
  style_description: '',
  values_priorities: '',
  decision_style: '',
  delegation_envelope: D1_DELEGATION_ENVELOPE,
  primary_language: 'en',
  language_overrides: {},
  language_rules: '',
};

/** Default values for each step (used when skipping) — matches backend STEP_DEFAULTS */
export const STEP_DEFAULTS = {
  operating_mode: { value: 'founder' },
  active_domains: {
    active_domains: [
      { domainId: 1, ledByAi: false },
      { domainId: 2, ledByAi: false },
      { domainId: 4, ledByAi: false },
      { domainId: 7, ledByAi: false },
    ],
  },
  ai_provider: { skipped: true },
  locale_tz: { timezone: 'Europe/Berlin', language: 'de-DE', currency: 'EUR' },
  cadence: {
    planning_cycle: 'quarterly',
    cadence_types: ['daily', 'weekly', 'monthly', 'quarterly', 'annual'],
  },
  summary: {},
} as const;

/** Operating mode descriptions */
export interface OperatingModeInfo {
  id: string;
  /** W2-G: function so t() is called at render time, not module-load time. */
  title: () => string;
  description: () => string;
  qcDensity: string;
  aiDefaults: string;
  cadence: string;
}

export const OPERATING_MODES: OperatingModeInfo[] = [
  {
    id: 'founder',
    title: () => t('Founder'),
    description: () => t('For early-stage companies where the founder is closely involved in every decision. Full quality confirmation on all deliverables.'),
    qcDensity: 'Every deliverable is reviewed before release',
    aiDefaults: 'Shadow mode — AI drafts, humans execute',
    cadence: 'Daily optional, Weekly required',
  },
  {
    id: 'growth',
    title: () => t('Growth'),
    description: () => t('For scaling companies with growing teams. Tiered quality confirmation based on priority — full for critical items, lite for lower priority.'),
    qcDensity: 'High-priority items fully reviewed, others get a quick check',
    aiDefaults: 'Shadow/Copilot — AI assists, humans approve',
    cadence: 'Daily recommended, Weekly + Monthly required',
  },
  {
    id: 'scaling',
    title: () => t('Scaling'),
    description: () => t('For mature organizations with established processes. Sampling-based quality confirmation with full oversight only on critical items.'),
    qcDensity: 'Critical items fully reviewed, others spot-checked',
    aiDefaults: 'Copilot/Autopilot — AI executes within boundaries',
    cadence: 'All cadences required',
  },
];

/** Domain info for selection */
export interface DomainInfo {
  domain_id: number;
  /** W2-G: function so t() is called at render time, not module-load time. */
  name: () => string;
  description: () => string;
  icon: string;
  defaultActive: boolean;
  required: boolean;
}

export const DOMAIN_LIST: DomainInfo[] = [
  { domain_id: 1, name: () => t('Strategy'), description: () => t('Vision, mission, and strategic direction'), icon: 'Compass', defaultActive: true, required: true },
  { domain_id: 2, name: () => t('Marketing'), description: () => t('Brand, content, campaigns, and market presence'), icon: 'Megaphone', defaultActive: true, required: false },
  { domain_id: 3, name: () => t('Sales'), description: () => t('Revenue generation, pipeline, and customer acquisition'), icon: 'TrendingUp', defaultActive: false, required: false },
  { domain_id: 4, name: () => t('Product'), description: () => t('Product development, roadmap, and feature delivery'), icon: 'Package', defaultActive: true, required: false },
  { domain_id: 5, name: () => t('Engineering'), description: () => t('Technical architecture, code quality, and infrastructure'), icon: 'Code', defaultActive: false, required: false },
  { domain_id: 6, name: () => t('Customer'), description: () => t('Customer success, support, and retention'), icon: 'Users', defaultActive: false, required: false },
  { domain_id: 7, name: () => t('Finance & Legal'), description: () => t('Financial planning, compliance, and legal operations'), icon: 'Landmark', defaultActive: true, required: false },
  { domain_id: 8, name: () => t('People'), description: () => t('HR, culture, talent acquisition, and development'), icon: 'Heart', defaultActive: false, required: false },
  { domain_id: 9, name: () => t('Administration'), description: () => t('Operations, facilities, and internal processes'), icon: 'Building2', defaultActive: false, required: false },
  { domain_id: 10, name: () => t('Quality'), description: () => t('Quality assurance, standards, and continuous improvement'), icon: 'Shield', defaultActive: false, required: false },
  { domain_id: 11, name: () => t('Operational Excellence'), description: () => t('Process optimization, lean operations, and efficiency'), icon: 'Zap', defaultActive: false, required: false },
];

/** Common locale options */
export interface LocaleOption {
  value: string;
  label: string;
}

export const COMMON_LOCALES: LocaleOption[] = [
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'fr-FR', label: 'Français (France)' },
  { value: 'es-ES', label: 'Español (España)' },
  { value: 'it-IT', label: 'Italiano (Italia)' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'nl-NL', label: 'Nederlands (Nederland)' },
  { value: 'pl-PL', label: 'Polski (Polska)' },
  { value: 'sv-SE', label: 'Svenska (Sverige)' },
  { value: 'da-DK', label: 'Dansk (Danmark)' },
  { value: 'nb-NO', label: 'Norsk bokmål (Norge)' },
  { value: 'fi-FI', label: 'Suomi (Suomi)' },
  { value: 'ja-JP', label: '日本語 (日本)' },
  { value: 'ko-KR', label: '한국어 (대한민국)' },
  { value: 'zh-CN', label: '中文 (简体)' },
];

/** Common currency options */
export const COMMON_CURRENCIES: LocaleOption[] = [
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'GBP', label: 'GBP — British Pound (£)' },
  { value: 'CHF', label: 'CHF — Swiss Franc (Fr.)' },
  { value: 'SEK', label: 'SEK — Swedish Krona (kr)' },
  { value: 'NOK', label: 'NOK — Norwegian Krone (kr)' },
  { value: 'DKK', label: 'DKK — Danish Krone (kr)' },
  { value: 'PLN', label: 'PLN — Polish Złoty (zł)' },
  { value: 'JPY', label: 'JPY — Japanese Yen (¥)' },
  { value: 'CAD', label: 'CAD — Canadian Dollar (C$)' },
  { value: 'AUD', label: 'AUD — Australian Dollar (A$)' },
  { value: 'BRL', label: 'BRL — Brazilian Real (R$)' },
  { value: 'RSD', label: 'RSD — Serbian Dinar (RSD)' },
  { value: 'CZK', label: 'CZK — Czech Koruna (Kč)' },
  { value: 'HUF', label: 'HUF — Hungarian Forint (Ft)' },
  { value: 'RON', label: 'RON — Romanian Leu (lei)' },
  { value: 'BGN', label: 'BGN — Bulgarian Lev (лв)' },
  { value: 'TRY', label: 'TRY — Turkish Lira (₺)' },
  { value: 'INR', label: 'INR — Indian Rupee (₹)' },
  { value: 'CNY', label: 'CNY — Chinese Yuan (¥)' },
];

/** Common timezones */
export const COMMON_TIMEZONES: LocaleOption[] = [
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/São Paulo (BRT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
];

/** Cadence schedule info for step 5 */
export interface CadenceScheduleInfo {
  type: string;
  label: string;
  description: string;
  defaultTime: string;
  defaultDay: string;
  defaultActive: boolean;
  alwaysActive: boolean;
  recommendedFor: string[];
}

export const CADENCE_SCHEDULES: CadenceScheduleInfo[] = [
  {
    type: 'daily',
    label: 'Daily Check-in',
    description: 'State scan, blockers, escalations, Control Tower hygiene',
    defaultTime: '09:00',
    defaultDay: 'weekdays',
    defaultActive: true,
    alwaysActive: false,
    recommendedFor: ['growth', 'scaling'],
  },
  {
    type: 'weekly',
    label: 'Weekly Operating Review',
    description: 'Thresholds, initiative health, QC trends, FTT tracking',
    defaultTime: '10:00',
    defaultDay: 'Monday',
    defaultActive: true,
    alwaysActive: false,
    recommendedFor: ['founder', 'growth', 'scaling'],
  },
  {
    type: 'monthly',
    label: 'Monthly System Review',
    description: 'Principles updates, OS metrics, Mode Check, readiness assessment',
    defaultTime: '14:00',
    defaultDay: '1st Monday',
    defaultActive: true,
    alwaysActive: false,
    recommendedFor: ['growth', 'scaling'],
  },
  {
    type: 'quarterly',
    label: 'Quarterly Outcome Reset',
    description: 'Retrospective + forward planning for the next quarter',
    defaultTime: '14:00',
    defaultDay: 'Last week',
    defaultActive: true,
    alwaysActive: false,
    recommendedFor: ['founder', 'growth', 'scaling'],
  },
  {
    type: 'trimester',
    label: 'Trimester Outcome Reset',
    description: 'Retrospective + forward planning for the next trimester',
    defaultTime: '14:00',
    defaultDay: 'Last week',
    defaultActive: false,
    alwaysActive: false,
    recommendedFor: ['founder', 'growth', 'scaling'],
  },
  {
    type: 'annual',
    label: 'Annual Strategy Review',
    description: 'Full strategy review, annual goal setting, OS maturity assessment',
    defaultTime: '10:00',
    defaultDay: 'December',
    defaultActive: true,
    alwaysActive: true,
    recommendedFor: ['founder', 'growth', 'scaling'],
  },
];
